/**
 * High-Variant Product Support
 * Enables products with >250 variants to work correctly
 * Uses Shopify Section Rendering API for dynamic variant fetching
 *
 * @see https://shopify.dev/docs/storefronts/themes/product-merchandising/variants/support-high-variant-products
 */

theme.HighVariantSupport = (function() {
  'use strict';

  /**
   * Check if current product is in high-variant mode
   */
  function isHighVariantMode(container) {
    var variantJson = container.querySelector('[data-variant-json]');
    return variantJson && variantJson.hasAttribute('data-high-variant-mode');
  }

  /**
   * Get selected option value IDs from the form
   */
  function getSelectedOptionValueIds(container) {
    var inputs = container.querySelectorAll('[data-variant-input]:checked, select[data-variant-input]');
    var ids = [];

    inputs.forEach(function(input) {
      var id;
      if (input.tagName === 'SELECT') {
        var selectedOption = input.options[input.selectedIndex];
        id = selectedOption ? selectedOption.dataset.optionValueId : null;
      } else {
        id = input.dataset.optionValueId;
      }
      if (id) {
        ids.push(id);
      }
    });

    return ids;
  }

  /**
   * Fetch variant data using Section Rendering API
   */
  function fetchVariantByOptions(productUrl, optionValueIds, sectionId) {
    var params = '';
    if (optionValueIds.length > 0) {
      params = '?option_values=' + optionValueIds.join(',') + '&section_id=' + sectionId;
    } else {
      params = '?section_id=' + sectionId;
    }

    return fetch(productUrl + params)
      .then(function(response) {
        return response.text();
      })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');

        // Extract current variant JSON from fetched section
        var variantJson = doc.querySelector('[data-current-variant-json]');
        if (variantJson) {
          try {
            return JSON.parse(variantJson.innerHTML);
          } catch (e) {
            console.error('High-variant support: Failed to parse variant JSON', e);
            return null;
          }
        }
        return null;
      })
      .catch(function(error) {
        console.error('High-variant support: Fetch failed', error);
        return null;
      });
  }

  /**
   * Update option value availability UI after fetching new state
   */
  function updateOptionAvailability(container, doc) {
    // Find all variant input wrappers in the fetched document
    var newInputs = doc.querySelectorAll('[data-variant-input]');

    newInputs.forEach(function(newInput) {
      var optionValueId = newInput.dataset.optionValueId;
      if (!optionValueId) return;

      // Find corresponding input in current container
      var currentInput = container.querySelector('[data-option-value-id="' + optionValueId + '"]');
      if (!currentInput) return;

      // Update disabled state
      var isDisabled = newInput.classList.contains('disabled') || newInput.disabled;
      var label = container.querySelector('label[for="' + currentInput.id + '"]');

      if (isDisabled) {
        currentInput.classList.add('disabled');
        if (label) label.classList.add('disabled');
      } else {
        currentInput.classList.remove('disabled');
        if (label) label.classList.remove('disabled');
      }
    });
  }

  /**
   * Handle variant change for high-variant products
   * Returns a promise that resolves with the variant data
   */
  function handleVariantChange(container, srcElement) {
    if (!isHighVariantMode(container)) {
      return Promise.resolve(null);
    }

    var optionValueIds = getSelectedOptionValueIds(container);
    var sectionEl = container.closest('[data-section-id]');
    var sectionId = sectionEl ? sectionEl.dataset.sectionId : null;
    var productUrl = window.location.pathname;

    if (!sectionId) {
      console.error('High-variant support: Could not find section ID');
      return Promise.resolve(null);
    }

    return fetchVariantByOptions(productUrl, optionValueIds, sectionId);
  }

  // Public API
  return {
    isHighVariantMode: isHighVariantMode,
    getSelectedOptionValueIds: getSelectedOptionValueIds,
    fetchVariantByOptions: fetchVariantByOptions,
    updateOptionAvailability: updateOptionAvailability,
    handleVariantChange: handleVariantChange
  };
})();
